import { useMemo, useState } from "react";
import MiniFilterBar from "./parts//MiniFilterBar";
import RolePill from "./parts/RolePill";
import StaffSelect from "./parts/StaffSelect";
import { WF } from "../../utils/workflow";
import { formatForCell } from "../../utils/norms"

/**
 * DraftsStep – the “step 1” table inside the wizard.
 *
 * Props:
 * - tasks: Array of reassignment task objects
 * - filters: { q: string, role: "All" | "CP" | "CM" | "SP" | "SM" }
 * - onChangeFilters: fn(newFilters)
 * - drafts: Map(taskId -> { task, newStaffIdx })
 * - onPick: fn(task, newStaffIdx)
 * - empByIndex: Map<number, EmployeeRow>
 * - isAdmin: boolean
 * - userToName: Map<username -> "First Last">
 * - formatWfTs: (ts) => { display, title } | null
 */
export default function DraftsStep({
  tasks,
  filters,
  onChangeFilters,
  drafts,
  onPick,
  onDelete,
  empByIndex,
  nameToIndex,
  isAdmin,
  userToName,
  clientRolesByCode,
  wizardLoading,
  // formatWfTs,
}) {
  const [sortCfg, setSortCfg] = useState({ key: "clientCode", dir: "asc" });

  function toggleSort(key) {
    setSortCfg(prev => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  }

  // Sortable column header — renders a <th> with click-to-sort + arrow indicator
  function thSort(label, field) {
    const active = sortCfg.key === field;
    return (
      <th
        className={`rwz-th-sort${active ? " sorted" : ""}`}
        onClick={() => toggleSort(field)}
        title={`Sort by ${label}`}
      >
        {label}
        <span className="rwz-sort-icon">
          {active ? (sortCfg.dir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </th>
    );
  }

  // sort by configurable column
  const sortedTasks = useMemo(() => {
    const copy = [...tasks];
    const { key, dir } = sortCfg;
    copy.sort((a, b) => {
      const av = String(a[key] ?? "").toLowerCase();
      const bv = String(b[key] ?? "").toLowerCase();
      return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return copy;
  }, [tasks, sortCfg]);

  // mini filterbar: search + role + status
  const filteredTasks = useMemo(() => {
    const q = (filters.q || "").toLowerCase().trim();

    return sortedTasks.filter((t) => {
      if (filters.role !== "All" && t.role !== filters.role) return false;
      // Status filter: default undefined/null to "To be reassigned"
      if (filters.status && filters.status !== "All") {
        const effectiveStatus = t.wfStatus || (t.isElective ? WF.ELECTIVE : "To be reassigned");
        if (effectiveStatus !== filters.status) return false;
      }
      if (!q) return true;
      const hay = `${t.clientCode} ${t.clientName} ${t.currentStaffName}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sortedTasks, filters]);

  // Count elective tasks still waiting for metadata to be seeded
  const seedingCount = useMemo(
    () => filteredTasks.filter(t => t.isElective && !t.wfStatus).length,
    [filteredTasks]
  );

  // username → nice display
  const displayUser = (username) => {
    if (!username) return "—";
    const un = String(username).toLowerCase();
    if (un === "sysadmin") return "RPRS Committee";
    return userToName.get(un) || username;
  };

  function ClientRolesHover({ clientCode, clientName, roles = [] }) {
    if (!roles.length) {
      // No extra info; just render the name as-is
      return <span>{clientName}</span>;
    }

    return (
      <span className="rwz-client-hover">
        {clientName}
        <div className="rwz-hover-card">
          <div className="rwz-hover-title">
            {clientCode} · {clientName}
          </div>
          {roles.map((r) => (
            <div
              key={`${r.role}|${r.service || ""}|${r.staffIdx}`}
              className="rwz-hover-row"
            >
              <div className="rwz-hover-role">
                <RolePill role={r.role} service={r.service} />
              </div>
              <div className="rwz-hover-staff">
                {r.staffName || "—"}
              </div>
            </div>
          ))}
        </div>
      </span>
    );
  }


  return (
    <>
      <MiniFilterBar value={filters} onChange={onChangeFilters} />
      <div style={{ position: "relative" }}>
        {wizardLoading && (
          <div className="drfts-loading-overlay">
            <div className="drfts-spinner" />
            <span className="drfts-loading-text">Preparing tasks…</span>
          </div>
        )}
        <table className="rwz-table">
        <thead>
          <tr>
            {thSort("Client Code", "clientCode")}
            {thSort("Client Name", "clientName")}
            {thSort("Role", "role")}
            {thSort("Current Staff", "currentStaffName")}
            <th>Reassignment</th>
            {isAdmin && (
              <>
                <th>Assigned By</th>
                <th>Assigned To</th>
                <th>Assigned On</th>
                {thSort("Status", "wfStatus")}
              </>
            )}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filteredTasks.map((t) => (
            <tr key={t.id} className={t.wfStatus === WF.DRAFT_REJECTED ? "rwz-rejected-row" : ""}>
              <td className="mono">{t.clientCode}</td>
              {/* <td>{t.clientName}</td> */}
              <td>
                <ClientRolesHover
                  clientCode={t.clientCode}
                  clientName={t.clientName}
                  roles={clientRolesByCode?.get(t.clientCode)?.items || []}
                />
              </td>
              {/* <td><RolePill role={t.role} tags={t.serviceTags} /></td> */}
              <td><RolePill role={t.role} service={t.service} /></td>
              <td>{t.currentStaffName}</td>
              <td>
                {(() => {
                  // Used to lock picklist if status is needs review or accepted
                  const locked = t.wfStatus === WF.DRAFT_NEEDS_REVIEW || t.wfStatus === WF.DRAFT_ACCEPTED;
                  const excelSeed = (t.wfStatus === WF.EXCEL_IMPORT)
                    ? (t.wfPendingIdx ?? "")
                    : "";
                  const value = locked
                    ? (t.wfPendingIdx ?? "")
                    : (drafts.get(t.id)?.newStaffIdx ?? excelSeed ?? "");
                  const onChange = (idx) => { if (!locked) onPick(t, idx); };
                  const rejectedTip = (!isAdmin && t.wfStatus === WF.DRAFT_REJECTED && t.wfPendingStaff)
                    ? `Your previous selection of "${t.wfPendingStaff}" was rejected by ${displayUser(t.wfFrom)} on ${formatForCell(t.wfUpdated).text}`
                    : undefined;
                  return (
                    <div title={rejectedTip}>
                      <StaffSelect
                        value={value}
                        onChange={onChange}
                        empByIndex={empByIndex}
                        disabled={locked}
                      />
                    </div>
                  );
                })()}
              </td>

              {isAdmin && (
                <>
                  <td>{displayUser(t.wfFrom) ?? <span className="ab-muted">—</span>}</td>
                  <td>{displayUser(t.wfTo) ?? <span className="ab-muted">—</span>}</td>
                  <td>
                    {(() => {
                      const { text, title } = formatForCell(t.wfUpdated);
                      return <span title={title}>{text}</span>;   // cell shows date; hover shows date+time in user’s TZ
                    })()}
                  </td>
                  <td>{t.wfStatus ?? <span className="ab-muted">—</span>}</td>
                </>
              )}
              <td>
                <button
                  className="rwz-delete-btn"
                  title={`Delete task: ${t.clientCode} (${t.role})`}
                  onClick={() => onDelete(t)}
                >
                  −
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {seedingCount > 0 && (
        <span className="ab-muted" style={{ marginLeft: 8, display: "inline-block", marginTop: 4 }}>
          Seeding {seedingCount} metadata…
        </span>
      )}
      </div>
    </>
  );
}
